/**
 * Modal Demande de Crédits - Procédure holistique B2B
 * Qualification client selon normes reconnues et gestion des risques
 */

import { X, CreditCard, FileText, Users, TrendingUp, Brain, Heart, Shield, Target, CheckCircle, Clock, Scale, Search, FileCheck } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useState } from 'react';

interface DemandeCreditsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const tabs = [
  { id: 'overview', label: 'Vue d\'ensemble', icon: Target },
  { id: 'procedure', label: 'Procédure Détaillée', icon: FileText },
  { id: 'qualification', label: 'Qualification Client', icon: Search },
  { id: 'gouvernance', label: 'Gouvernance', icon: Shield },
  { id: 'esprit', label: 'Esprit & Philosophie', icon: Brain },
  { id: 'psychologie', label: 'Psychologie & Impact', icon: Heart },
  { id: 'outils', label: 'Outils & Templates', icon: FileText },
  { id: 'kpis', label: 'KPIs & Métriques', icon: TrendingUp },
];

export default function DemandeCreditsModal({ isOpen, onClose }: DemandeCreditsModalProps) {
  const [activeTab, setActiveTab] = useState('overview');

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl my-8 max-h-[90vh] flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white p-8 relative">
              <button
                onClick={onClose}
                className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-full transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                  <CreditCard className="w-8 h-8" />
                </div>
                <div>
                  <h2 className="text-3xl font-bold mb-2">Demande de Crédits</h2>
                  <p className="text-indigo-100 text-lg">
                    Qualification client selon normes reconnues - Gestion des risques & croissance équilibrée
                  </p>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200 bg-gray-50">
              <div className="flex overflow-x-auto">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`
                        flex items-center gap-2 px-6 py-4 text-sm font-medium transition-colors whitespace-nowrap
                        ${activeTab === tab.id
                          ? 'text-indigo-600 border-b-2 border-indigo-600 bg-white'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                        }
                      `}
                    >
                      <Icon className="w-4 h-4" />
                      {tab.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-8">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeTab}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  {activeTab === 'overview' && <OverviewTab />}
                  {activeTab === 'procedure' && <ProcedureTab />}
                  {activeTab === 'qualification' && <QualificationTab />}
                  {activeTab === 'gouvernance' && <GouvernanceTab />}
                  {activeTab === 'esprit' && <EspritTab />}
                  {activeTab === 'psychologie' && <PsychologieTab />}
                  {activeTab === 'outils' && <OutilsTab />}
                  {activeTab === 'kpis' && <KPIsTab />}
                </motion.div>
              </AnimatePresence>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

// Vue d'ensemble
function OverviewTab() {
  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-6 border border-indigo-200">
        <h3 className="text-2xl font-bold text-gray-900 mb-4">🎯 Objectif de la Procédure</h3>
        <p className="text-gray-700 text-lg leading-relaxed">
          Établir un système de <strong>qualification client selon normes reconnues</strong> (Dun & Bradstreet, Equifax, Coface, 
          ou système interne) qui permet de déterminer les <strong>termes de crédit appropriés</strong> (NET 30, NET 45, NET 60, etc.) 
          selon le niveau de risque. Cette procédure <strong>protège BZ</strong> tout en permettant la <strong>croissance commerciale</strong>.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
              <Shield className="w-6 h-6 text-red-600" />
            </div>
            <h4 className="text-xl font-bold text-gray-900">Gestion des Risques</h4>
          </div>
          <ul className="space-y-2 text-gray-700">
            <li className="flex items-start gap-2">
              <CheckCircle className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" />
              <span>Qualification selon normes reconnues</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" />
              <span>Évaluation du risque financier</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" />
              <span>Termes de crédit adaptés au risque</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" />
              <span>Limites de crédit selon profil</span>
            </li>
          </ul>
        </div>

        <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-blue-600" />
            </div>
            <h4 className="text-xl font-bold text-gray-900">Croissance Commerciale</h4>
          </div>
          <ul className="space-y-2 text-gray-700">
            <li className="flex items-start gap-2">
              <CheckCircle className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" />
              <span>Processus d'approbation rapide</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" />
              <span>Flexibilité selon profil client</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" />
              <span>Support aux ventes dans la négociation</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" />
              <span>Croissance sécurisée et mesurée</span>
            </li>
          </ul>
        </div>
      </div>

      <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
        <h4 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
          <Scale className="w-6 h-6 text-blue-600" />
          L'Équilibre BZ
        </h4>
        <p className="text-gray-700 leading-relaxed">
          Cette procédure reconnaît que <strong>l'administration doit protéger BZ</strong> en évaluant rigoureusement les risques. 
          Elle reconnaît aussi que <strong>les ventes doivent pouvoir négocier</strong> des termes compétitifs pour gagner des clients. 
          Cette procédure crée un cadre où protection et croissance coexistent harmonieusement.
        </p>
      </div>
    </div>
  );
}

// Procédure détaillée
function ProcedureTab() {
  const steps = [
    {
      id: 1,
      title: "Initiation de la Demande",
      description: "Qui peut initier une demande de crédit et dans quels contextes",
      details: [
        "Nouveau client : Ventes initie la demande avant signature contrat",
        "Augmentation limite : Client existant demande plus de crédit",
        "Retard de paiement : Admin initie révision du crédit",
        "Renouvellement annuel : Révision systématique du profil"
      ],
      icon: FileCheck,
      color: "blue"
    },
    {
      id: 2,
      title: "Collecte d'Informations",
      description: "Données nécessaires pour la qualification",
      details: [
        "Informations légales : Nom, adresse, numéro d'entreprise",
        "Informations financières : Revenus, historique de paiement, références bancaires",
        "Informations commerciales : Secteur, taille, années d'activité",
        "Références : Autres fournisseurs, banques, clients"
      ],
      icon: FileText,
      color: "green"
    },
    {
      id: 3,
      title: "Qualification selon Normes",
      description: "Évaluation du risque selon grille standardisée",
      details: [
        "Consultation services externes : Dun & Bradstreet, Equifax, Coface",
        "Analyse selon grille BZ : Score de risque calculé",
        "Attribution niveau : Faible, Moyen, Élevé",
        "Documentation complète du processus"
      ],
      icon: Search,
      color: "purple"
    },
    {
      id: 4,
      title: "Recommandation Termes de Crédit",
      description: "Proposition de termes selon niveau de risque",
      details: [
        "Risque Faible : NET 30 jours standard",
        "Risque Moyen : NET 30 avec garantie ou NET 45",
        "Risque Élevé : Paiement anticipé ou garantie bancaire",
        "Limite de crédit selon profil et historique"
      ],
      icon: Target,
      color: "yellow"
    },
    {
      id: 5,
      title: "Processus d'Approbation",
      description: "Validation hiérarchique selon montant et risque",
      details: [
        "Crédit ≤ 10k : Approbation Administration",
        "Crédit 10k-50k : Approbation Admin + Ventes",
        "Crédit {'>'} 50k : Approbation Direction",
        "Délai de traitement : 24-48h maximum"
      ],
      icon: Shield,
      color: "orange"
    },
    {
      id: 6,
      title: "Communication & Suivi",
      description: "Information client et monitoring continu",
      details: [
        "Communication claire des termes approuvés",
        "Documentation dans ERP/Système",
        "Révision trimestrielle des profils",
        "Ajustements selon performance de paiement"
      ],
      icon: Users,
      color: "indigo"
    }
  ];

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-6 border border-indigo-200">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">📋 Procédure Complète</h3>
        <p className="text-gray-700">
          Processus structuré en 6 étapes, de l'initiation à la communication et au suivi.
        </p>
      </div>

      <div className="space-y-6">
        {steps.map((step, index) => {
          const Icon = step.icon;
          const colorClasses = {
            blue: 'bg-blue-100 text-blue-600',
            green: 'bg-green-100 text-green-600',
            yellow: 'bg-yellow-100 text-yellow-600',
            orange: 'bg-orange-100 text-orange-600',
            red: 'bg-red-100 text-red-600',
            purple: 'bg-purple-100 text-purple-600',
            indigo: 'bg-indigo-100 text-indigo-600',
          };
          const bgClass = colorClasses[step.color as keyof typeof colorClasses]?.split(' ')[0] || 'bg-gray-100';
          const textClass = colorClasses[step.color as keyof typeof colorClasses]?.split(' ')[1] || 'text-gray-600';
          return (
            <motion.div
              key={step.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 ${bgClass} rounded-xl flex items-center justify-center flex-shrink-0`}>
                  <Icon className={`w-6 h-6 ${textClass}`} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-sm font-bold text-gray-500">Étape {step.id}</span>
                    <h4 className="text-xl font-bold text-gray-900">{step.title}</h4>
                  </div>
                  <p className="text-gray-700 mb-4">{step.description}</p>
                  <ul className="space-y-2">
                    {step.details.map((detail, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-gray-600">
                        <CheckCircle className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" />
                        <span>{detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

// Qualification Client
function QualificationTab() {
  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-200">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">🔍 Qualification Client selon Normes</h3>
        <p className="text-gray-700">
          Système de qualification basé sur des normes reconnues pour évaluer le risque client de manière objective et standardisée.
        </p>
      </div>

      <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
        <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <FileCheck className="w-6 h-6 text-purple-600" />
          Normes de Qualification Recommandées
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
            <h5 className="font-semibold text-blue-900 mb-2">Dun & Bradstreet</h5>
            <p className="text-sm text-blue-700">Score PAYDEX, Rating de crédit, Indicateurs de risque</p>
          </div>
          <div className="bg-green-50 rounded-lg p-4 border border-green-200">
            <h5 className="font-semibold text-green-900 mb-2">Equifax</h5>
            <p className="text-sm text-green-700">Score commercial, Historique de paiement, Dettes</p>
          </div>
          <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
            <h5 className="font-semibold text-purple-900 mb-2">Coface</h5>
            <p className="text-sm text-purple-700">Rating international, Analyse sectorielle, Risque pays</p>
          </div>
        </div>
        <p className="text-gray-600 text-sm italic">
          Note : BZ peut également utiliser un système interne de qualification basé sur les mêmes critères si les services 
          externes ne sont pas disponibles ou pour compléter l'analyse.
        </p>
      </div>

      <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
        <h4 className="text-xl font-bold text-gray-900 mb-4">Grille de Qualification BZ</h4>
        <div className="space-y-4">
          <div className="bg-green-50 rounded-lg p-5 border border-green-200">
            <h5 className="font-bold text-green-900 mb-3 text-lg">🟢 Risque Faible</h5>
            <div className="space-y-2 text-sm text-green-800 mb-3">
              <p><strong>Critères :</strong></p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Score D&B PAYDEX ≥ 80 ou équivalent</li>
                <li>Historique de paiement excellent (0-5 jours de retard)</li>
                <li>Entreprise établie ≥ 5 ans</li>
                <li>Revenus stables ou en croissance</li>
                <li>Pas de dettes significatives</li>
              </ul>
            </div>
            <div className="bg-white rounded p-3 border border-green-300">
              <p className="font-semibold text-green-900">Termes Recommandés :</p>
              <p className="text-green-700">NET 30 jours standard, limite de crédit jusqu'à 3x CA mensuel moyen</p>
            </div>
          </div>

          <div className="bg-yellow-50 rounded-lg p-5 border border-yellow-200">
            <h5 className="font-bold text-yellow-900 mb-3 text-lg">🟡 Risque Moyen</h5>
            <div className="space-y-2 text-sm text-yellow-800 mb-3">
              <p><strong>Critères :</strong></p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Score D&B PAYDEX 50-79 ou équivalent</li>
                <li>Historique de paiement acceptable (6-30 jours de retard occasionnel)</li>
                <li>Entreprise établie 2-5 ans</li>
                <li>Revenus variables mais viables</li>
                <li>Dettes modérées et gérées</li>
              </ul>
            </div>
            <div className="bg-white rounded p-3 border border-yellow-300">
              <p className="font-semibold text-yellow-900">Termes Recommandés :</p>
              <p className="text-yellow-700">NET 30 avec garantie ou NET 45, limite de crédit jusqu'à 2x CA mensuel moyen</p>
            </div>
          </div>

          <div className="bg-red-50 rounded-lg p-5 border border-red-200">
            <h5 className="font-bold text-red-900 mb-3 text-lg">🔴 Risque Élevé</h5>
            <div className="space-y-2 text-sm text-red-800 mb-3">
              <p><strong>Critères :</strong></p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Score D&B PAYDEX &lt; 50 ou équivalent</li>
                <li>Historique de paiement problématique (&gt; 30 jours de retard fréquent)</li>
                <li>Entreprise nouvelle &lt; 2 ans ou instable</li>
                <li>Revenus incertains ou en déclin</li>
                <li>Dettes significatives ou problèmes financiers</li>
              </ul>
            </div>
            <div className="bg-white rounded p-3 border border-red-300">
              <p className="font-semibold text-red-900">Termes Recommandés :</p>
              <p className="text-red-700">Paiement anticipé, garantie bancaire, ou NET 30 avec limite très réduite</p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
        <h4 className="text-xl font-bold text-gray-900 mb-3">📝 Processus de Qualification</h4>
        <ol className="space-y-3 text-gray-700">
          <li className="flex items-start gap-2">
            <span className="font-bold text-blue-600">1.</span>
            <span>Collecte des informations client (formulaire standardisé)</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="font-bold text-blue-600">2.</span>
            <span>Consultation service de qualification externe (D&B, Equifax, Coface)</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="font-bold text-blue-600">3.</span>
            <span>Analyse selon grille BZ (score composite)</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="font-bold text-blue-600">4.</span>
            <span>Attribution niveau de risque (Faible/Moyen/Élevé)</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="font-bold text-blue-600">5.</span>
            <span>Recommandation termes de crédit selon niveau</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="font-bold text-blue-600">6.</span>
            <span>Documentation complète dans système BZ</span>
          </li>
        </ol>
      </div>
    </div>
  );
}

// Gouvernance
function GouvernanceTab() {
  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-6 border border-purple-200">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">🛡️ Structure de Gouvernance</h3>
        <p className="text-gray-700">
          Rôles, responsabilités et processus décisionnels pour une gestion équilibrée des demandes de crédit.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
          <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Users className="w-6 h-6 text-blue-600" />
            Rôles & Responsabilités
          </h4>
          <div className="space-y-4">
            <div>
              <h5 className="font-semibold text-gray-900 mb-2">Ventes (Antoine/Jérôme)</h5>
              <ul className="space-y-1 text-gray-700 text-sm">
                <li>• Initiation demande crédit nouveau client</li>
                <li>• Collecte informations client</li>
                <li>• Présentation dossier à l'administration</li>
                <li>• Communication termes au client</li>
                <li>• Négociation si nécessaire</li>
              </ul>
            </div>
            <div>
              <h5 className="font-semibold text-gray-900 mb-2">Administration (LAH/Mélissa)</h5>
              <ul className="space-y-1 text-gray-700 text-sm">
                <li>• Qualification client selon normes</li>
                <li>• Analyse risque financier</li>
                <li>• Recommandation termes de crédit</li>
                <li>• Approbation crédits ≤ 10k</li>
                <li>• Documentation dans système</li>
              </ul>
            </div>
            <div>
              <h5 className="font-semibold text-gray-900 mb-2">Direction</h5>
              <ul className="space-y-1 text-gray-700 text-sm">
                <li>• Approbation crédits {'>'} 50k</li>
                <li>• Arbitrage cas exceptionnels</li>
                <li>• Révision politique de crédit</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
          <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Shield className="w-6 h-6 text-purple-600" />
            Seuils d'Approbation
          </h4>
          <div className="space-y-4">
            <div className="bg-green-50 rounded-lg p-4 border border-green-200">
              <h5 className="font-semibold text-green-900 mb-2">Crédit ≤ 10,000$</h5>
              <p className="text-sm text-green-700">Approbation : Administration</p>
              <p className="text-xs text-green-600 mt-1">Délai : 24h</p>
            </div>
            <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
              <h5 className="font-semibold text-yellow-900 mb-2">Crédit 10,001$ - 50,000$</h5>
              <p className="text-sm text-yellow-700">Approbation : Administration + Ventes</p>
              <p className="text-xs text-yellow-600 mt-1">Délai : 48h</p>
            </div>
            <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
              <h5 className="font-semibold text-orange-900 mb-2">Crédit {'>'} 50,000$</h5>
              <p className="text-sm text-orange-700">Approbation : Direction</p>
              <p className="text-xs text-orange-600 mt-1">Délai : 48-72h</p>
            </div>
            <div className="bg-red-50 rounded-lg p-4 border border-red-200">
              <h5 className="font-semibold text-red-900 mb-2">Cas Exceptionnels</h5>
              <p className="text-sm text-red-700">Approbation : Direction uniquement</p>
              <p className="text-xs text-red-600 mt-1">Réunion d'arbitrage requise</p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
        <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Clock className="w-6 h-6 text-indigo-600" />
          Réunions de Gouvernance
        </h4>
        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" />
            <div>
              <h5 className="font-semibold text-gray-900">Réunion Mensuelle Crédits</h5>
              <p className="text-sm text-gray-600">Revue des demandes en cours, décisions d'approbation, ajustements procédures</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" />
            <div>
              <h5 className="font-semibold text-gray-900">Réunion Trimestrielle Stratégique</h5>
              <p className="text-sm text-gray-600">Analyse des KPIs, révision des profils clients, amélioration continue</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Esprit & Philosophie
function EspritTab() {
  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-6 border border-indigo-200">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">💭 L'Esprit de la Procédure</h3>
        <p className="text-gray-700">
          Cette procédure n'est pas un frein à la croissance. C'est un cadre de protection qui permet de grandir de manière sécurisée.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
          <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Shield className="w-6 h-6 text-red-600" />
            L'Esprit Administration
          </h4>
          <p className="text-gray-700 mb-4 leading-relaxed">
            L'administration n'est <strong>pas "méchante"</strong>. Elle doit protéger BZ en évaluant rigoureusement les risques. 
            Chaque crédit accordé, c'est un risque financier. Sans qualification, c'est l'aveuglement. Avec qualification, 
            c'est la protection.
          </p>
          <p className="text-gray-700 leading-relaxed">
            L'administration a besoin de <strong>normes claires</strong> et de <strong>processus structurés</strong> pour faire 
            son travail efficacement. Elle ne peut pas "sentir" le risque - elle doit le mesurer.
          </p>
        </div>

        <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
          <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Users className="w-6 h-6 text-blue-600" />
            L'Esprit Ventes
          </h4>
          <p className="text-gray-700 mb-4 leading-relaxed">
            Les ventes ne manquent <strong>pas de courage entrepreneurial</strong>. Elles doivent pouvoir négocier des termes 
            compétitifs pour gagner des clients. Chaque refus de crédit peut tuer une vente. Chaque flexibilité peut créer 
            un partenariat à long terme.
          </p>
          <p className="text-gray-700 leading-relaxed">
            Les ventes ont besoin de <strong>processus rapides</strong> et de <strong>flexibilité raisonnable</strong> pour faire 
            leur travail. Sans cadre, c'est le chaos. Avec cadre, c'est la confiance.
          </p>
        </div>
      </div>

      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-6 border border-indigo-200">
        <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Scale className="w-6 h-6 text-indigo-600" />
          L'Équilibre BZ
        </h4>
        <p className="text-gray-700 leading-relaxed mb-4">
          Cette procédure crée un <strong>pont entre ces deux réalités</strong>. Elle donne à l'administration les outils pour 
          évaluer les risques de manière objective selon des normes reconnues. Elle donne aux ventes un processus rapide et 
          transparent pour obtenir l'approbation. Elle crée un langage commun où les deux équipes peuvent collaborer.
        </p>
        <p className="text-gray-700 leading-relaxed">
          <strong>L'objectif n'est pas de bloquer les ventes</strong>. C'est de créer un système où protection et croissance 
          coexistent harmonieusement pour le bien de BZ et de ses clients.
        </p>
      </div>
    </div>
  );
}

// Psychologie & Impact
function PsychologieTab() {
  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-pink-50 to-rose-50 rounded-xl p-6 border border-pink-200">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">🧠 Comprendre la Psychologie</h3>
        <p className="text-gray-700">
          Chaque équipe a ses motivations, ses peurs, et ses besoins. Comprendre cela, c'est créer de l'empathie et réduire les tensions.
        </p>
      </div>

      <div className="space-y-6">
        <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
          <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Heart className="w-6 h-6 text-red-600" />
            La Psychologie de l'Administration
          </h4>
          <div className="space-y-4">
            <div>
              <h5 className="font-semibold text-gray-900 mb-2">💪 Forces & Motivations</h5>
              <ul className="space-y-1 text-gray-700 text-sm">
                <li>• <strong>Protection</strong> : Garder BZ financièrement sain</li>
                <li>• <strong>Rigueur</strong> : Évaluer les risques objectivement</li>
                <li>• <strong>Responsabilité</strong> : Protéger les ressources de l'équipe</li>
                <li>• <strong>Normes</strong> : Utiliser des standards reconnus</li>
              </ul>
            </div>
            <div>
              <h5 className="font-semibold text-gray-900 mb-2">😰 Peurs & Frustrations</h5>
              <ul className="space-y-1 text-gray-700 text-sm">
                <li>• <strong>Manque de patience</strong> : Les processus longs créent du stress</li>
                <li>• <strong>Sentiment d'être "méchante"</strong> : Refuser un crédit est désagréable</li>
                <li>• <strong>Manque de support</strong> : Sentir que les ventes ne comprennent pas</li>
                <li>• <strong>Risque financier</strong> : Peur de mauvaises décisions</li>
              </ul>
            </div>
            <div>
              <h5 className="font-semibold text-gray-900 mb-2">💡 Ce dont elle a besoin</h5>
              <ul className="space-y-1 text-gray-700 text-sm">
                <li>• <strong>Normes claires</strong> : Grille de qualification objective</li>
                <li>• <strong>Support des ventes</strong> : Compréhension et collaboration</li>
                <li>• <strong>Reconnaissance</strong> : Comprendre qu'elle protège BZ</li>
                <li>• <strong>Outils efficaces</strong> : Accès aux services de qualification</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
          <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Heart className="w-6 h-6 text-blue-600" />
            La Psychologie des Ventes
          </h4>
          <div className="space-y-4">
            <div>
              <h5 className="font-semibold text-gray-900 mb-2">💪 Forces & Motivations</h5>
              <ul className="space-y-1 text-gray-700 text-sm">
                <li>• <strong>Croissance</strong> : Développer les ventes</li>
                <li>• <strong>Relation client</strong> : Bâtir la confiance</li>
                <li>• <strong>Flexibilité</strong> : S'adapter aux besoins clients</li>
                <li>• <strong>Rapidité</strong> : Répondre rapidement aux opportunités</li>
              </ul>
            </div>
            <div>
              <h5 className="font-semibold text-gray-900 mb-2">😰 Peurs & Frustrations</h5>
              <ul className="space-y-1 text-gray-700 text-sm">
                <li>• <strong>Perte de vente</strong> : Refus de crédit = client perdu</li>
                <li>• <strong>Sentiment de manquer de courage</strong> : Ne pas oser négocier</li>
                <li>• <strong>Conflit avec admin</strong> : Sentir qu'on est "contre" l'admin</li>
                <li>• <strong>Processus lent</strong> : Délais qui font perdre des ventes</li>
              </ul>
            </div>
            <div>
              <h5 className="font-semibold text-gray-900 mb-2">💡 Ce dont elle a besoin</h5>
              <ul className="space-y-1 text-gray-700 text-sm">
                <li>• <strong>Processus rapide</strong> : Délais de 24-48h maximum</li>
                <li>• <strong>Support de l'admin</strong> : Compréhension des besoins client</li>
                <li>• <strong>Reconnaissance</strong> : Comprendre qu'elle développe BZ</li>
                <li>• <strong>Outils de négociation</strong> : Templates et arguments</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
          <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Brain className="w-6 h-6 text-indigo-600" />
            L'Impact de la Compréhension
          </h4>
          <p className="text-gray-700 leading-relaxed mb-4">
            Quand l'administration comprend que les ventes ne manquent pas de courage mais naviguent une réalité commerciale 
            complexe, elle peut être plus rapide dans ses évaluations. Quand les ventes comprennent que l'administration n'est 
            pas "méchante" mais protège BZ selon des normes objectives, elles peuvent être plus collaboratives.
          </p>
          <p className="text-gray-700 leading-relaxed">
            Cette procédure crée cet espace de compréhension mutuelle. Elle transforme un conflit potentiel en collaboration 
            structurée. Elle permet aux deux équipes de travailler ensemble pour protéger BZ tout en permettant la croissance.
          </p>
        </div>
      </div>
    </div>
  );
}

// Outils & Templates
function OutilsTab() {
  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-cyan-50 to-blue-50 rounded-xl p-6 border border-cyan-200">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">🛠️ Outils & Templates</h3>
        <p className="text-gray-700">
          Ressources pratiques pour faciliter l'application de la procédure au quotidien.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
          <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <FileText className="w-6 h-6 text-blue-600" />
            Formulaire de Demande de Crédit
          </h4>
          <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
            <p className="font-semibold text-gray-900">Informations Requises :</p>
            <ul className="space-y-1 text-gray-600">
              <li>• Nom légal et adresse complète</li>
              <li>• Numéro d'entreprise (NEQ)</li>
              <li>• Revenus annuels (3 dernières années)</li>
              <li>• Historique de paiement (références)</li>
              <li>• Informations bancaires</li>
              <li>• Secteur d'activité et années d'opération</li>
            </ul>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
          <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Target className="w-6 h-6 text-green-600" />
            Grille de Qualification
          </h4>
          <div className="space-y-3">
            <div className="bg-green-50 rounded-lg p-3 border border-green-200">
              <h5 className="font-semibold text-green-900 mb-1">Risque Faible</h5>
              <p className="text-xs text-green-700">NET 30, limite 3x CA mensuel</p>
            </div>
            <div className="bg-yellow-50 rounded-lg p-3 border border-yellow-200">
              <h5 className="font-semibold text-yellow-900 mb-1">Risque Moyen</h5>
              <p className="text-xs text-yellow-700">NET 30+garantie ou NET 45, limite 2x CA</p>
            </div>
            <div className="bg-red-50 rounded-lg p-3 border border-red-200">
              <h5 className="font-semibold text-red-900 mb-1">Risque Élevé</h5>
              <p className="text-xs text-red-700">Paiement anticipé ou garantie bancaire</p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
        <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <TrendingUp className="w-6 h-6 text-purple-600" />
          Systèmes & Intégrations
        </h4>
        <ul className="space-y-2 text-gray-700">
          <li className="flex items-start gap-2">
            <CheckCircle className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" />
            <span>ERP avec module crédit intégré</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" />
            <span>Intégration Dun & Bradstreet API</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" />
            <span>Workflow d'approbation automatisé</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" />
            <span>Dashboard crédits en temps réel</span>
          </li>
        </ul>
      </div>
    </div>
  );
}

// KPIs & Métriques
function KPIsTab() {
  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-200">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">📊 KPIs & Métriques</h3>
        <p className="text-gray-700">
          Indicateurs clés pour mesurer l'efficacité de la gestion des demandes de crédit.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
          <h4 className="text-xl font-bold text-gray-900 mb-4">Indicateurs Principaux</h4>
          <div className="space-y-4">
            <div>
              <h5 className="font-semibold text-gray-900 mb-1">Délai Moyen d'Approbation</h5>
              <p className="text-sm text-gray-600">Objectif : &lt; 36h</p>
              <p className="text-xs text-gray-500 mt-1">Temps entre demande et décision</p>
            </div>
            <div>
              <h5 className="font-semibold text-gray-900 mb-1">Taux d'Approbation</h5>
              <p className="text-sm text-gray-600">Objectif : 70-80%</p>
              <p className="text-xs text-gray-500 mt-1">% de demandes approuvées</p>
            </div>
            <div>
              <h5 className="font-semibold text-gray-900 mb-1">Taux de Défaut Crédit</h5>
              <p className="text-sm text-gray-600">Objectif : &lt; 2%</p>
              <p className="text-xs text-gray-500 mt-1">% de crédits non recouvrés</p>
            </div>
            <div>
              <h5 className="font-semibold text-gray-900 mb-1">Montant Total Crédits Actifs</h5>
              <p className="text-sm text-gray-600">Objectif : &lt; 15% du CA annuel</p>
              <p className="text-xs text-gray-500 mt-1">Exposition totale au risque</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
          <h4 className="text-xl font-bold text-gray-900 mb-4">Indicateurs Secondaires</h4>
          <div className="space-y-4">
            <div>
              <h5 className="font-semibold text-gray-900 mb-1">Distribution par Niveau de Risque</h5>
              <p className="text-sm text-gray-600">Objectif : 60% Faible, 30% Moyen, 10% Élevé</p>
            </div>
            <div>
              <h5 className="font-semibold text-gray-900 mb-1">Taux de Révision Annuelle</h5>
              <p className="text-sm text-gray-600">Objectif : 100%</p>
              <p className="text-xs text-gray-500 mt-1">% de profils révisés annuellement</p>
            </div>
            <div>
              <h5 className="font-semibold text-gray-900 mb-1">Satisfaction Équipes</h5>
              <p className="text-sm text-gray-600">Objectif : &gt; 4/5</p>
              <p className="text-xs text-gray-500 mt-1">Sondage trimestriel Admin + Ventes</p>
            </div>
            <div>
              <h5 className="font-semibold text-gray-900 mb-1">Taux d'Utilisation Services Externes</h5>
              <p className="text-sm text-gray-600">Objectif : &gt; 90%</p>
              <p className="text-xs text-gray-500 mt-1">% de qualifications avec D&B/Equifax</p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
        <h4 className="text-xl font-bold text-gray-900 mb-4">📈 Révision Trimestrielle</h4>
        <p className="text-gray-700 leading-relaxed">
          Chaque trimestre, l'équipe Admin + Ventes + Direction se réunit pour analyser ces KPIs, identifier les tendances, 
          et ajuster les procédures si nécessaire. Cette révision continue assure que la procédure reste efficace et équilibrée.
        </p>
      </div>
    </div>
  );
}

